import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';

export type TabType = 'mine' | 'unassigned' | 'all' | 'favorites';

interface Tab {
    id: TabType;
    label: string;
}

const ALL_TABS: Tab[] = [
    { id: 'unassigned', label: 'Não atribuído' },
    { id: 'mine', label: 'Meus' },
    { id: 'all', label: 'Tudo' },
];

const MEMBER_TABS: Tab[] = [
    { id: 'unassigned', label: 'Não atribuído' },
    { id: 'mine', label: 'Meus' },
    { id: 'favorites', label: 'Favoritos' },
];

interface SidebarTabsProps {
    activeTab: TabType;
    onTabChange: (tab: TabType) => void;
    counters?: Partial<Record<TabType, number>>;
    userRole?: 'owner' | 'admin' | 'member' | 'viewer';
}

export const SidebarTabs: React.FC<SidebarTabsProps> = ({
    activeTab,
    onTabChange,
    counters,
    userRole = 'member',
}) => {
    const visibleTabs = useMemo(() => (
        userRole === 'owner' || userRole === 'admin' ? ALL_TABS : MEMBER_TABS
    ), [userRole]);

    return (
        <nav aria-label="Filtrar conversas">
            <ul className="m-0 flex w-full list-none gap-1.5 p-0">
                {visibleTabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    const count = counters?.[tab.id] ?? 0;
                    const showCount = count > 0;

                    return (
                        <li key={tab.id} className="flex min-w-0 flex-1">
                            <button
                                type="button"
                                aria-pressed={isActive}
                                aria-label={showCount ? `${tab.label}, ${count}` : tab.label}
                                id={`chat-tab-${tab.id}`}
                                title={tab.label}
                                onClick={() => onTabChange(tab.id)}
                                className={cn(
                                    'chip min-h-9 w-full min-w-0 touch-manipulation justify-center px-2 leading-normal md:min-h-7',
                                    isActive && 'active',
                                )}
                            >
                                <span className="min-w-0 truncate">{tab.label}</span>
                                {showCount ? (
                                    <span
                                        className={cn(
                                            'inline-flex min-h-[17px] min-w-[17px] shrink-0 items-center justify-center',
                                            'rounded-full px-1.5 font-mono text-2xs font-bold leading-none tabular-nums',
                                            'bg-danger-500 text-destructive-foreground',
                                        )}
                                        aria-hidden="true"
                                    >
                                        {count > 99 ? '99+' : count}
                                    </span>
                                ) : null}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};
